import { asPartialTheme } from "@/theme/types";

import { commonTheme } from "./common";

const colorPalette = {
  white: "#FFFFFF",
  primary: "#FBF3F0",
  secondary: "#F0D1D7", // ligthRed, 240, 209, 215, infocard bg
  greyCoal: "#585858",
  warning: "#FF1C1C",
  error: "#E31717",
  gray3: "#FBF3F0",
  gray1: "#F4F4F4",
  gray2: "#E2E0DD",
  gray5: "#444349",
  black: "#101010",
  green: "#00A744", // green, 0, 167, 167
  gray8: "#CCC9BF", // 204, 201, 191 = 596
  gray7: "#CAC5BF", // 202, 197, 191 = 590
  gray11: "#B9B6B6", // 185, 182, 182 = 549
  gray10: "#4F4F4F", // 79, 79, 79 = 237
  gray6: "#2D2D2D", // 45, 45, 45 = 135
  gray9: "#272727", // 39, 39, 39 = 117
};

export const commonLightTheme = asPartialTheme({
  ...commonTheme,
  color: {
    primary: "#FBF3F0",
    mediumGray: "#E2E0DD",
    black: "#2D2D2D",
    defaultText: "#444349",
    error: "#FF1C1C",
    qrButtonColor: colorPalette.white,
    // topNavBar
    topNavBarText: colorPalette.white,
    topNavBarBg: colorPalette.white,
    topNavBarTableText: colorPalette.black,
    topNavBarTableBg: colorPalette.white,
    topNavBarBgImgTopGradient:
      "linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,0) 100%)",
    topNavBarBgImgBottomGradient:
      "linear-gradient(0deg, #FFFFFF 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,0) 100%)",
    // topCategories
    topCategoriesHeadingText: "#2D2D2D",
    topCategoriesBg: colorPalette.white,
    topCategoriesActiveText: "#101010",
    topCategoriesMenuButtonBg: "#010101",
    topCategoriesMenuButtonText: "#FFFFFF",
    // horizontalScrollable
    horizontalScrollableButtonBg: "#FFFFFF",
    // subCategories
    subCategoriesHeadingText: "#2D2D2D",
    subCategoriesText: "#989898",
    subCategoriesTextActive: "#2D2D2D",
    subCategoriesBg: "#FFFFFF",
    // productRow
    productRowTitle: "#2D2D2D",
    productRowPrice: "#2D2D2D",
    productRowDescription: "#444349",
    productRowBg: "#FFFFFF",
    productRowAmountBoxShadow: "unset",
    productRowAmountBg: colorPalette.black,
    productRowFeaturedBg: "#C83C42",
    productRowFeaturedText: "#FFFFFF",
    // amount button
    amountBtnBorder: colorPalette.gray2,
    amountBtnBg: "transparent",
    amountBtnReversedBg: colorPalette.black,
    amountBtnReversedText: colorPalette.white,
    // burgerMenu
    burgerMenuBg: "#FFFFFF",
    burgerMenuBorders: "#E2E0DD",
    burgerMenuText: "#000000",
    burgerMenuUserNameText: "#101010",
    burgerMenuUserStatusText: "#444349",
    burgerMenuIcons: "#2D2D2D",
    burgerMenuDropdownIcon: "#2D2D2D",
    burgerMenuDropdownIconDisabled: "#E1E1E1",
    burgerMenuLoginButtonBg: colorPalette.white,
    burgerMenuLoginButtonText: "#2D2D2D",
    burgerMenuLogoutButtonBg: colorPalette.white,
    burgerMenuLogoutButtonText: "#2D2D2D",
    burgerMenuLogoutButtonBorder: "#CAC5BF",
    burgerMenuItemTop: "#FAFAFA",
    burgerMenuBoxShadow: "0 -5px 10px #fafafa",
    burgerMenuItemLabel: colorPalette.greyCoal,
    burgerMenuAccountLogIn: "#101010",
    burgerMenuBtnBg: colorPalette.black,
    burgerMenuBtnText: colorPalette.white,
    burgerMenuOutlineBtnBorder: colorPalette.black,

    // modals
    modalBg: "#FFFFFF",
    modalText: "#444349",
    modalQrHelpText: "#585858",
    modalQrText: "#2D2D2D",
    modalQrButtonBg: colorPalette.white,
    modalQrButtonBorder: colorPalette.black,
    modalQrButtonIcon: "#2D2D2D",
    modalQrScannerBg: "#FBF3F0",
    modalQrScannerText: "#444349",
    modalQrScannerIcon: "#444349",
    modalQrScannerError: "#E31717",
    modalQrScannerOuterContainerBg: colorPalette.white,
    modalQrScannerPreviewBg: colorPalette.white,
    modalQrScannerCloseBtnBg: colorPalette.white,
    modalQrScannerPreviewContentText: colorPalette.greyCoal,
    modalQrScannerPreviewContentPText: "#444349",
    modalQRScannerLink: colorPalette.black,
    modalQrTableNrBg: colorPalette.white,
    modalLoginBg: "#FBF3F0",
    modalLoginText: "#444349",
    modalLoginTitle: "#101010",
    modalLoginHintText: "#444349",
    modalLoginErrorBg: "rgba(242, 129, 25, 0.24)",
    modalLoginErrorText: "#444349",
    modalDeleteBg: "#FFFFFF",
    modalDeleteText: "#2D2D2D",
    modalSearchBg: "#FFFFFF",
    // fields
    fieldBg: colorPalette.white,
    fieldBorder: "#C2C2C2",
    fieldBorderFocused: "#585858",
    fieldErrorBorder: "#E31717",
    fieldText: "#444349",
    fieldTextPlaceholder: colorPalette.gray11,
    fieldIcon: "#2D2D2D",
    fieldIconBg: "#E2E0DD",
    fieldLabelText: "#989898",
    // productPage
    productPageBoxTitle: "#2D2D2D",
    productPageBoxPrice: "#2D2D2D",
    productPageBoxDescription: "#444349",
    productPageHeadingText: "#444349",
    productPageDescriptionText: "#2D2D2D",
    productPageImageTitle: "#585858",
    productPageMandatoryOption: "#828282",
    productPageShapeBlockBorder: "#C2C2C2",
    productPageShapeBlockLabel: "#585858",
    productPageShapeBlockActiveBg: colorPalette.black,
    productPageShapeBlockActiveText: colorPalette.white,
    // buttons
    buttonText: colorPalette.white,
    buttonBg: "#2D2D2D",
    buttonTransparentBg: colorPalette.white,
    buttonTransparentText: colorPalette.black,
    buttonTransparentBorder: colorPalette.black,
    disabledButtonBg: "#E9E9E9",
    disabledButtonText: "#989898",
    requestPermissionBtnBg: "#1E1E1E",
    // productAddSuccess
    productAddSuccessBg: colorPalette.white,
    productAddSuccessText: "#585858",
    // contactInfoModal+
    contactInfoModalLabel: colorPalette.black,
    contactInfoModalFooterBg: colorPalette.white,
    contactInfoMessageLabel: "#989898",
    contactInfoTitle: "#585858",
    // cartPage+
    white: colorPalette.white,
    greyCoal: "#585858",
    // modals+
    modalOverlay: "rgba(0,0,0,0.4)",
    // loginSuccessModal
    loginSuccessModalBg: colorPalette.white,
    loginSuccessModalText: colorPalette.gray6,
    loginSuccessModalIcon: colorPalette.green,
    fieldIconGreen: colorPalette.green,
    fieldCountryListText: colorPalette.black,
    // productPage+
    productPageSpecialNeedsFieldBg: "white",
    productPageSpecialNeedsFieldBorder: colorPalette.gray7,
    productPageSpecialNeedsComment: "#989898",
    productPageSpecialNeedsTitle: "#444349",
    // cartPage+
    cartPageRowTitle: "#585858",
    cartPageRowPrice: "#585858",
    cartPageRowTrashIcon: "rgba(0, 0, 0, 0.24)",
    cartPageItemBlockBorder: "#E9E9E9",
    cartPageConfirmBlockBg: "#F7F7F7",
    cartPageConfirmBlockText: colorPalette.black,
    cartPageItemsTitle: colorPalette.black,
    cartPageTotalBlock: colorPalette.black,
    // qttBlock+
    qttText: colorPalette.greyCoal,
    qttActiveIcon: colorPalette.greyCoal,
    qttActiveReversedIcon: colorPalette.gray2,
    qttInactiveIcon: colorPalette.gray2,
    qttInactiveReversedIcon: colorPalette.greyCoal,
    // orderStatusPage+
    orderStatusPageText: colorPalette.gray5,
    orderStatusPageRowText: colorPalette.black,
    orderStatusPageLineText: colorPalette.black,
    orderStatusPageOrderNr: colorPalette.black,
    orderStatusPageEmail: "unset",
    orderStatusPageExtraSalesTitle: colorPalette.greyCoal,
    orderStatusPagePickUpText: colorPalette.greyCoal,
    orderStatusPageLocationText: colorPalette.gray5,
    // loadingScreen+
    loadingScreenBg: "rgba(0,0,0,0.4)",
    // warning
    warningBg: "#C83C42",
    // sign-in block
    signInBackground:
      "linear-gradient(129.21deg, #FF8002 5.17%, #F2BA4C 94.4%)",
    signInBlockBg: "#F2F2F2",
    signedInBlockBg: "transparent",
    signedInBlockH5: "#585858",
    signedInBlockH4: "inherit",
    signedInBlockSwitchAccount: "inherit",
    signedInBlockSwitchBtn: "inherit",
    signedInBlockBorder: "transparent",
    // menu block
    menuBlockTitle: "#6B6B6B",
    menuBlockSearchBg: "#F2F2F2",
    menuBlockSearch: "#585858",
    menuBlockArrowBg: "#FFFFFF",
    menuBlockSearchIcon: "#828282",
    // search menu
    searchMenuDot: "#C2C2C2",
    searchCategoriesText: colorPalette.greyCoal,
    searchMenuCloseIcon: "#FFFFFF",
    // box component
    boxShadowTop: "0px -8px 10px rgba(0, 0, 0, 0.025)",
    boxShadowGlobal: "0px 0px 12px 2px rgba(0, 0, 0, 0.05)",
    boxListBg: "#FFFFFF",
    boxBg: "#FFFFFF",
    // footer
    footerBg: "#F7F7F7",
    footerBoxShadow: "0px -8px 10px rgba(0, 0, 0, 0.025)",
    footerMenuBtnStroke: "#FFFFFF",
    footerCartIconActive: colorPalette.white,
    footerCartIconInactive: colorPalette.black,
    // brand
    brandLocationTitle: "#333333",
    // error page
    errorPageTitle: colorPalette.black,
    errorPageText: "#444349",
    // payment failed
    paymentFailedText: "#F2F2F2",
    checkoutCancelBtnBg: "#F2F2F2",
    checkoutCancelBtn: "#101010",
    // language dropdown
    languageDropdownBg: "rgba(255, 255, 255, 0.40)",
    languageDropdownButtonBg: "rgba(255, 255, 255, 0.54)",
    languageDropdownButtonText: "#585858",
    languageDropdownSelectedButtonText: "#101010",
    languageDropdownButtonHoverText: "#101010",
    languageDropdownButtonHoverBg: "rgba(255, 255, 255, 0.60)",
    languageDropdownSelectedLanguageBg: "rgba(255, 255, 255, 0.80)",
    //location box
    locationBoxBg:
      "linear-gradient(90.59deg, #F5F5F5 0.51%, rgba(248, 248, 248, 0.692708) 13.77%, rgba(255, 255, 255, 0) 31.87%)",
    locationBoxNavigation: "#C83C42",
    locationBoxHeader: "#333333",
    locationBoxText: "#585858",
    // location info modal
    locationInfoHeader: "#333333",
    locationText: "#585858",
    locationDescription: "#585858",
    // navbar
    navbarLoginButtonBg: "#101010",
    navbarLoginButtonText: "#FFFFFF",
    navbarDiningMethodButtonBg: "rgba(255,255,255, 0.5)",
    navbarDiningMethodButtonText: "#101010",
    navbarLoyaltyClub: "#585858",
    navbarCurrentLanguage: "#585858",
    navbarUser: "#585858",
    // banner
    bannerSuccessBackground: "#F4FFE5",
    bannerErrorBackground: "#FFEAE5",
    bannerSuccessTextColor: "#333333",
    bannerErrorTextColor: "#333333",
    // Status Pag
    orderStatusPageBg: "#FFFFFF",
    // order status card
    orderStatusCardBackground: colorPalette.white,
    orderStatusCardBackgroundHighlight: "#F4FFE5",
    orderStatusCardShadow: "0px 0px 12px 2px rgba(0, 0, 0, 0.04)",
    orderStatusCardInactiveBackgroundColor: "#F7F7F7",
    orderStatusCardPendingBackgroundColor: "#FFE8D2",
    orderStatusCardPaidBackgroundColor: "#D6FCD8",
    orderStatusCardInfoLabelTextColor: "#585858",
    orderStatusCardInfoValueTextColor: "#333333",
    orderStatusCardStatusRowTimeTextColor: "#101010",
    orderStatusCardPreOrderTitleTextColor: "#585858",
    orderStatusCardQueueNumberLabelTextColor: "#585858",
    orderStatusCardQueueNumberValueTextColor: "#333333",
    orderStatusCardOrderCardCompletedBlockTitleTextColor: "#585858",
    orderStatusCardOrderCardCompletedBlockSubtitleTextColor: "#333333",
    // cart section
    cartSectionButtonContainerBackground: colorPalette.white,
    cartSectionButtonContainerShadow: "0px 0px 3px 1px rgba(0,0,0,0.08)",
    //landing image
    landingImageBg: "#FFFFFF",
    //keyValueList
    lineKey: "#101010",
    lineValue: "#101010",
    // toggle
    toggleBackground: "#FFFFFF",
    toggleBorderColor: "#E9E9E9",
    toggleOptionColor: "#C2C2C2",
    toggleHoveredOptionColor: "#F2F2F2",
    toggleSelectedOptionColor: "#FFFFFF",
    toggleSelectedOptionBackground: "#EC213C",
    // dining option modal
    qrScannerButtonBackground: "#FFFFFF",
    infoTextColor: "#989898",
    // skeleton
    skeletonElementBg: colorPalette.gray1,
    skeletonElementShimmer:
      "linear-gradient(70deg, rgba(255,255,255,0) 20%, rgba(0,0,0,0.05) 49%, rgba(52,52,52,0) 80%)",
    // Retry component
    retryHintTextColor: "#585858",
    // Link component
    linkColor: "#444349",
    // Loader component
    loaderSpinnerColor: "#F68A1F",
    // ImageCarousel component
    imageCarouselDotColor: "#F68A1F",
    imageCarouselDotSelectedColor: "#F68A1F",
    imageCarouselDotSelectedProgressColor: "#ffa500",
  },
  blobStorage: {
    // Warning icon
    warningIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Caution_Small_thin.svg",
    // Error Page bgImage
    errorPageBg:
      "https://assets.markus.live/03-order-app/light/media/ERROR.svg",
    // Open Burger Menu - Bottom of the Menu
    markusLogo:
      "https://assets.markus.live/00-generic-app/logo/markus_footer_darkgray-color_130x46.svg",
    markusLogoBlack:
      "https://assets.markus.live/00-generic-app/logo/markus_footer_black-color_130x46.svg",
    // When Open Modals and Pages that act like a modal - Close button appear in top right corner
    closeModalIcon:
      "https://assets.markus.live/03-order-app/light/media/close.svg",
    // Icon to open burger menu
    burgerIcon:
      "https://assets.markus.live/03-order-app/light/icons/topbar/TopBar_Hamburger_thin_default.svg",
    // Icon to open burger menu, when having notification (red dot on it appears)
    burgerActiveIcon:
      "https://assets.markus.live/03-order-app/light/icons/topbar/TopBar_Hamburger_thin_active.svg",
    // Cart Icon, appears in burger menu for My Orders item
    cartIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Shopping_Cart_thin.svg",
    // QR icon that appears inside a text field, when you open QR code manual entry modal
    qrIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Scan_QR_thin.svg",
    // Appears in 2 places: Table Number block (located in the right side) on homepage, restaurant info page, cart page; and inside QR manual entry modal for one of the buttons
    scanQrIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Scan_QR_thin.svg",
    // Icon that indicates that field validation passed, located in login form - email field; if email is in valid format - it will display it
    validFieldIcon:
      "https://assets.markus.live/03-order-app/light/media/valid_field_green.svg",
    // Displayed on Product page. Product must have modifier.Choices in database
    extraChoiceIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Checker_checked_yes_checked-filled_yes.svg",
    orderPendingIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Timing_thin.svg",
    // Status in progress icon
    statusInProgressIcon:
      "https://assets.markus.live/03-order-app/light/media/Checker_progress.svg",
    // Status done icon
    statusReadyIcon:
      "https://assets.markus.live/03-order-app/light/icons/generic-icons/Checker_checked_yes_checked-filled_yes_black.svg",
    // Status not done icon
    statusNotReadyIcon:
      "https://assets.markus.live/03-order-app/light/icons/generic-icons/Checker_checked_no_black.svg",
    // Displayed when trying to log in and API returned an error (cannot log in)
    failIcon: "https://assets.markus.live/03-order-app/light/media/fail.svg",
    // Confirm Age Button on Cart Page - disabled state (unchecked)
    checkedWhiteBgSmIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Checker_checked_yes_checked-filled_no.svg",
    // Displayed in Burger menu for finished order when KDS system is disabled, in Burger Menu - language selection (current active langage), on order status page when step is completed, in product extras block on product page, in product shape (size) block on product page
    checkedRadioSmIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Checker_checked-yes_radio.svg",
    // Displayed in Burger Menu - language selection (inactive langage), on order status page when step is incompleted, in product extras block on product page, in product shape (size) block on product page
    emptyRadioSmIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Checker_active-yes_selected-no.svg",
    // Displayed on Login Success Modal (when you logged in successfully), when you added item to the cart
    successGrayIcon:
      "https://assets.markus.live/03-order-app/light/media/Checker_success.svg",
    // Displayed in Restaurant Info Block on homepage, and in Resturant Info Page
    locationIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Location_thin.svg",
    schedulingIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Scheduling_thin.svg",
    timeIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Time_thin.svg",
    // Displayed for warning block, when: login form has error, when age verification is required on product page and cart page
    triangleIconActive:
      "https://assets.markus.live/03-order-app/light/icons/generic-icons/Caution_Small_active.svg",
    // Burger Menu - Language menu block
    warningUnchecked:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Checker_Alert-yes.svg",
    warningChecked:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Checker_checked_yes_checked-filled_no.svg",
    caretRightThinIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Chevron_Forward_thin.svg",
    caretLeftThinIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Chevron_Back_thin.svg",
    caretDownThinIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Expand_thin.svg",
    caretUpThinIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Collapse_thin.svg",
    // In burger Menu - each item has arrow up / down - that's the icon, icon gets rotated by code
    dropMenuIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Expand.svg",
    // When you press on search button on homepage - it opens search modal - there will be input field where this icon is appeared inside of it
    magnifyingGlassIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Search_Small.svg",
    // Password input field - show password icon (displayed when password is invisible to user)
    showIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Show_Preview_thin.svg",
    // Password input field - hide password icon (displayed when password is visible to user)
    hideIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Hide_Preview_thin.svg",
    // In burger menu - logout button has the logout icon
    logoutIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Sign_Out_thin.svg",
    // Displayed on Cart page - user block, the left icon that looks like card
    smallLoyaltyClubCardIcon:
      "https://assets.markus.live/00-generic-app/media/ApolloClubCardSmall_74x48.svg",
    tinyLoyaltyClubCardIcon:
      "https://assets.markus.live/00-generic-app/media/ApolloClubCardSmall_62x40.svg",
    rotatedLoyaltyClubCardIcon:
      "https://assets.markus.live/01-loyalty-portal/media/ApolloClubCard_big_angled.svg",
    closeIcon:
      "https://assets.markus.live/03-order-app/light/icons/generic-icons/Close_Big_thin.svg",
    commentIcon:
      "https://assets.markus.live/00-generic-app/icons/small/Comments_thin.svg",
    cameraSwitchIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Switch_Camera_thin.svg",
    languageIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Language_Change_2_thin.svg",
    locationMap:
      "https://assets.markus.live/03-order-app/assets-%tenantKey%-%tenantLocation%/light/maps/map_%tenantKey%_%tenantLocation%.png",
    settingsIcon:
      "https://assets.markus.live/03-order-app/light/icons/generic-icons/Settings.svg",
    statusBannerImage:
      "https://assets.markus.live/03-order-app/assets-%tenantKey%-%tenantLocation%/light/media/SMS.webp",
    diningOptionIconDineIn:
      "https://assets.markus.live/03-order-app/light/media/Dinein_big.svg",
    diningOptionIconTakeaway:
      "https://assets.markus.live/03-order-app/light/media/Takeaway_big.svg",
    spinnerIconSmall:
      "https://assets.markus.live/03-order-app/light/media/loader_small_24.svg",
    exitPageIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Exit_Page_Small_thin.svg",
    copyIcon:
      "https://assets.markus.live/00-generic-app/icons/generic-icons/Copy_Small.svg",
    checkerWithBorderIcon:
      "https://assets.markus.live/00-generic-app/icons-dark-theme/generic-icons/Checker_checked_yes_checked-filled_no.svg",
    burgerMenuLinks: [],
  },
} as const);
